import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { queryClient } from 'api/reactQuery'
import { getSubdomainQueryKey, IdentityProviderProps } from 'api/modules/subdomains'
import { CookieCategory } from 'constants/global'
import * as H from 'history'
import { FormattedMessage } from 'locales/index'
import React from 'react'
import { connect } from 'react-redux'
import { Redirect, useHistory } from 'react-router-dom'
import { RegisterForm } from 'shared/components/common/RegisterForm'
import { Alert, GenericError, ErrorBoundary, Button } from '@commutifi-fe/ui'
import { useLoginWithRedirect } from 'shared/hooks/oauth'
import {
  defaultProcessSurveyError,
  defaultProcessSurveySuccess,
  ProcessSurveyErrorIds,
  useProcessSurvey
} from 'shared/hooks/useProcessSurvey'
import { IDPRegisterFormPayload, Score, RegisterFormPayload } from 'shared/interfaces'
import { useConfig } from 'shared/providers'
import { selectors as surveySelectors } from 'store/modules/enterpriseSurvey'
import { extractUrlSearchParams } from 'utils/index'
import { logger, identify } from 'utils/logRocket'
import RegisterLayout from 'views/Anonymous/views/Onboarding/layout/RegisterLayout'
import SurveyLayout from './layout/SurveyLayout'
import styles from './styles.module.scss'

interface OnboardingRegisterScreenProps {
  initialName: string
  initialEmail: string
  surveyId: string
  directOnboarding: boolean
  createSurveyAnswers: (
    payload: RegisterFormPayload,
    promise?: Promise<any>
  ) => { accountId: string } | { metrics: Omit<Score, 'id' | 'commuterScore'> }
  resetSurveyAnswers: () => void
  unansweredQuestions?: { id: string; section: number }[]
  location: H.Location
}

function OnboardingRegisterScreen({
  unansweredQuestions,
  surveyId,
  directOnboarding,
  initialEmail,
  initialName,
  location
}: OnboardingRegisterScreenProps) {
  const { cookieconsent, locale } = useConfig()
  const history = useHistory()
  const [errorMessageId, setErrorMessageId] = React.useState<`${ProcessSurveyErrorIds}`>(ProcessSurveyErrorIds.TryAgain)
  const { processSurvey, loading, error } = useProcessSurvey(surveyId, {
    onSuccess: defaultProcessSurveySuccess(history, location.pathname),
    onError: defaultProcessSurveyError(history, location.pathname, setErrorMessageId)
  })

  /**
   * Setup Login with IDP redirect. This flow will bring us back to this screen with a authorization code and
   * a state in the URL search params so we can use those to post a survey
   */
  const { loading: extractingLoginUrl, error: loginRedirectError, loginWithRedirect } = useLoginWithRedirect()
  const loginWithIdentityProvider = (providerId: string) => (values: { name: string }) => {
    loginWithRedirect(providerId, { targetUrl: location.pathname, resumeToTarget: true, name: values.name })
  }

  /**
   * Setup extracting authorization code + state from url and triggering submit
   * right away if we find those in the URL.
   */
  const identityProvider = queryClient.getQueryData<IdentityProviderProps>(getSubdomainQueryKey())

  logger.info('identityProvider: ', identityProvider)
  const hasIdentityProvider = identityProvider?.id
  const { code, nonce, state } = extractUrlSearchParams()
  const isSSOSurvey = Boolean(code) && Boolean(nonce) && Boolean(identityProvider?.id)
  logger.info('identityProvider: ', isSSOSurvey)
  const injectedName = state?.name

  React.useEffect(() => {
    if (isSSOSurvey && identityProvider?.id) {
      logger.info('Starting to submit IDP survey', isSSOSurvey)
      const submitSurveyWithIDP = async (account: IDPRegisterFormPayload) => {
        if (cookieconsent?.allowedCategory(CookieCategory.Analytics)) {
          identify({ name: account.name })
        }
        await processSurvey(account)
      }
      submitSurveyWithIDP({
        name: injectedName,
        authorizationCode: code,
        state: nonce,
        identityProviderId: identityProvider.id,
        preferredLocale: locale
      })
    }
    logger.info('End of submit IDP survey')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy, keep to be safe
  }, [injectedName, code, nonce, identityProvider?.id, isSSOSurvey])

  if (unansweredQuestions && unansweredQuestions.length > 0) {
    logger.info('Unanswered Questions redirecting to first incomplete step')
    return (
      <Redirect
        push
        to={{ pathname: `step${unansweredQuestions[0].section}`, state: { referrer: location.pathname } }}
      />
    )
  }

  const submitSurvey = (account: RegisterFormPayload) => {
    if (cookieconsent?.allowedCategory(CookieCategory.Analytics)) {
      identify({ email: account.email, name: account.name })
    }
    processSurvey({ ...account, preferredLocale: locale })
  }

  const handleGoBack = () => {
    history.goBack()
  }
  const handleSubmit = hasIdentityProvider ? loginWithIdentityProvider(identityProvider.id) : submitSurvey

  const registerHeadlineTitles = !directOnboarding
    ? {
        title: <FormattedMessage id="components.registerScreen.title" />,
        subTitle: <FormattedMessage id="components.registerScreen.subtitle" />
      }
    : {}
  const registerButtonText = hasIdentityProvider ? (
    <FormattedMessage id="auth.login.withProvider" values={{ providerName: identityProvider.name }} />
  ) : !directOnboarding ? (
    <FormattedMessage id="global.complete" />
  ) : null

  return (
    <SurveyLayout className={styles['survey-layout--no-bottom-margin']}>
      <RegisterLayout
        header={
          <Button
            type="text"
            fitToContent
            aria-label="Go back to survey"
            onClick={handleGoBack}
            className={styles['header-button']}
            style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        }
        {...registerHeadlineTitles}
      >
        <ErrorBoundary ErrorComponent={GenericError}>
          <div className="u-flex-column u-justify-center u-flex-1">
            {(error || loginRedirectError) && errorMessageId ? (
              <div className="u-margin-bottom--medium">
                <Alert message={<FormattedMessage id={errorMessageId} />} type="error" closable />
              </div>
            ) : null}
            <RegisterForm
              initialName={initialName || injectedName}
              initialEmail={initialEmail}
              initialAgreement={isSSOSurvey}
              onSubmit={handleSubmit}
              loading={loading || extractingLoginUrl || (isSSOSurvey && !error && !errorMessageId)}
              registerButtonText={registerButtonText}
              registerButtonDisabled={isSSOSurvey ? false : undefined}
            >
              {!hasIdentityProvider && <RegisterForm.EmailField />}
              {directOnboarding && !hasIdentityProvider ? <RegisterForm.PasswordField /> : null}
            </RegisterForm>
          </div>
        </ErrorBoundary>
      </RegisterLayout>
    </SurveyLayout>
  )
}

// TODO
// - See if we have an identity provider to display the SSO sign on
// - Get value of direct onboarding in org or enterprise entity stored in redux survey prefilled data
// - If direct onboarding we redirect to dashboard. Otherwise we redirect to a success screen
// We use any for now since the survey redux state file is not in typescript :(
const mapStateToProps = (state: any) => ({
  initialName: surveySelectors.makeGetPrefilledData('account.name')(state),
  initialEmail: surveySelectors.makeGetPrefilledData('account.email')(state),
  surveyId: surveySelectors.getSurveyId(state),
  directOnboarding: surveySelectors.getIsDirectOnboarding(state),
  unansweredQuestions: surveySelectors.getUnansweredQuestions(state)
})

export default connect<any, any>(mapStateToProps, null)(OnboardingRegisterScreen)
